import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Bell, 
  Package, 
  Tag, 
  MessageSquare, 
  Shield, 
  Trash2,
  Check,
  Clock,
  Filter,
  Mail,
  ChevronLeft,
  ChevronRight,
  Settings,
  RefreshCcw,
  X,
  AlertCircle,
  XCircle,
  CheckCircle2,
  AlertTriangle
} from "lucide-react";
import Navbar from "../../components/Navbar";

const NOTIFICATIONS = [
  {
    id: 1,
    type: "order",
    title: "New Order Received",
    message: "Order #12345 has been placed for $149.99",
    time: "2 minutes ago",
    isRead: false,
    icon: Package,
    color: "blue"
  },
  {
    id: 2,
    type: "promotion",
    title: "Flash Sale Alert!",
    message: "Your 'Summer Special' deal is trending with 500+ views",
    time: "1 hour ago",
    isRead: false,
    icon: Tag,
    color: "green"
  },
  {
    id: 3,
    type: "message",
    title: "New Customer Message",
    message: "John Doe: Is this item still available?",
    time: "3 hours ago",
    isRead: true,
    icon: MessageSquare,
    color: "purple"
  },
  {
    id: 4,
    type: "security",
    title: "Security Alert",
    message: "New login detected from Chrome on Windows",
    time: "1 day ago",
    isRead: true,
    icon: Shield,
    color: "red"
  },
  {
    id: 5,
    type: "security",
    title: "Security Alert",
    message: "New login detected from Chrome on Windows",
    time: "1 day ago",
    isRead: true,
    icon: Shield,
    color: "red"
  },
  {
    id: 6,
    type: "security",
    title: "Security Alert",
    message: "New login detected from Chrome on Windows",
    time: "1 day ago",
    isRead: true,
    icon: Shield,
    color: "red"
  },
  // Add more notifications as needed
];

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(4);
  const [showSettings, setShowSettings] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    notificationId: null
  });

  const filterOptions = [
    { value: "all", label: "All" },
    { value: "unread", label: "Unread" },
    { value: "order", label: "Orders" },
    { value: "promotion", label: "Promotions" },
    { value: "message", label: "Messages" },
    { value: "security", label: "Security" },
  ];

  const filteredNotifications = notifications.filter(notification => {
    if (filter === "all") return true;
    if (filter === "unread") return !notification.isRead;
    return notification.type === filter;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNotifications = filteredNotifications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(notification =>
      notification.id === id ? { ...notification, isRead: true } : notification
    ));
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmation({
      show: true,
      notificationId: id
    });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.notificationId) {
      setNotifications(notifications.filter(
        notification => notification.id !== deleteConfirmation.notificationId
      ));
      setDeleteConfirmation({ show: false, notificationId: null });
    }
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    },
    exit: { 
      opacity: 0, 
      x: -100, 
      scale: 0.8,
      transition: { duration: 0.2 }
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />
      
      {/* Main Content Area with New Layout */}
      <div className="container flex-1 px-4 py-6 mx-auto max-w-7xl">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Notifications</h1>
            <p className="mt-1 text-gray-600">Stay updated with your latest activities</p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setNotifications([...NOTIFICATIONS])}
              className="flex gap-2 items-center px-4 py-2 text-gray-700 bg-white rounded-lg shadow-sm transition-all hover:shadow-md"
            >
              <RefreshCcw size={16} />
              <span>Refresh</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={clearAllNotifications}
              className="flex gap-2 items-center px-4 py-2 text-red-600 bg-red-50 rounded-lg transition-all hover:bg-red-100"
            >
              <X size={16} />
              <span>Clear All</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="col-span-12 md:col-span-3"
          >
            <div className="overflow-hidden bg-white rounded-xl shadow-sm">
              {/* Stats Section */}
              <div className="p-4 border-b">
                <h3 className="mb-4 font-medium text-gray-800">Overview</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total</span>
                    <span className="font-medium">{notifications.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Unread</span>
                    <span className="font-medium text-blue-600">
                      {notifications.filter(n => !n.isRead).length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Filters Section */}
              <div className="p-4">
                <h3 className="mb-4 font-medium text-gray-800">Filters</h3>
                {filterOptions.map((option) => (
                  <motion.button
                    key={option.value}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      filter === option.value
                        ? "bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                    onClick={() => setFilter(option.value)}
                  >
                    <div className="flex gap-3 items-center">
                      {option.value === 'all' && <Bell size={16} />}
                      {option.value === 'unread' && <Mail size={16} />}
                      {option.value === 'order' && <Package size={16} />}
                      {option.value === 'promotion' && <Tag size={16} />}
                      {option.value === 'message' && <MessageSquare size={16} />}
                      {option.value === 'security' && <Shield size={16} />}
                      {option.label}
                      {option.value === 'unread' && (
                        <span className="px-2 py-0.5 ml-auto text-xs bg-white bg-opacity-20 rounded-full">
                          {notifications.filter(n => !n.isRead).length}
                        </span>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Main Content Area */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="col-span-12 md:col-span-9"
          >
            {/* Notifications List */}
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {currentNotifications.map(notification => (
                  <motion.div
                    key={notification.id}
                    variants={itemVariants}
                    layout
                    className={`bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-300 border-l-4 ${
                      notification.isRead ? "border-gray-200" : `border-${notification.color}-500`
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex gap-4">
                        <motion.div 
                          whileHover={{ scale: 1.1 }}
                          className={`p-3 rounded-full bg-opacity-20 aspect-square flex items-center justify-center
                            ${notification.color === 'blue' ? 'bg-blue-100' : ''}
                            ${notification.color === 'green' ? 'bg-green-100' : ''}
                            ${notification.color === 'purple' ? 'bg-purple-100' : ''}
                            ${notification.color === 'red' ? 'bg-red-100' : ''}
                          `}
                        >
                          <notification.icon
                            size={20}
                            className={`
                              ${notification.color === 'blue' ? 'text-blue-600' : ''}
                              ${notification.color === 'green' ? 'text-green-600' : ''}
                              ${notification.color === 'purple' ? 'text-purple-600' : ''}
                              ${notification.color === 'red' ? 'text-red-600' : ''}
                            `}
                          />
                        </motion.div>
                        <div className="flex-1">
                          <h3 className="mb-1 font-semibold text-gray-800">
                            {notification.title}
                          </h3>
                          <p className="text-sm leading-relaxed text-gray-600">
                            {notification.message}
                          </p>
                          <div className="flex gap-2 items-center mt-2 text-xs text-gray-500">
                            <Clock size={12} />
                            <span>{notification.time}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {!notification.isRead && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-2 text-gray-500 hover:text-[#FF6C6C] rounded-full hover:bg-gray-100 transition-colors"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <Check size={16} />
                          </motion.button>
                        )}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-gray-500 rounded-full transition-colors hover:text-red-500 hover:bg-gray-100"
                          onClick={() => handleDeleteClick(notification.id)}
                        >
                          <Trash2 size={16} />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Pagination Controls */}
              {filteredNotifications.length > itemsPerPage && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex gap-2 justify-center items-center mt-8"
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-full ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <ChevronLeft size={20} />
                  </motion.button>

                  {/* Page Numbers */}
                  <div className="flex gap-2">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      
                      // Show first page, last page, current page, and one page before and after current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <motion.button
                            key={pageNumber}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => paginate(pageNumber)}
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                              currentPage === pageNumber
                                ? 'bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {pageNumber}
                          </motion.button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="flex justify-center items-center w-10 h-10 text-gray-400"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-full ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <ChevronRight size={20} />
                  </motion.button>
                </motion.div>
              )}

              {/* Empty State */}
              {filteredNotifications.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="py-16 text-center bg-white rounded-xl shadow-md"
                >
                  <motion.div
                    animate={{ 
                      y: [0, -10, 0],
                      rotate: [-5, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <Bell size={64} className="mx-auto mb-6 text-gray-400" />
                  </motion.div>
                  <h3 className="text-xl font-medium text-gray-600">No notifications</h3>
                  <p className="mt-2 text-gray-500">
                    You're all caught up! Check back later for new updates.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Settings Modal (optional) */}
      <AnimatePresence>
        {showSettings && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 justify-center items-center bg-black bg-opacity-50"
          >
            {/* Add settings modal content here */}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmation.show && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative p-6 mx-4 w-full max-w-md bg-white rounded-xl shadow-xl"
            >
              {/* Close button */}
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setDeleteConfirmation({ show: false, notificationId: null })}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XCircle size={20} />
              </motion.button>

              {/* Icon Animation Container */}
              <motion.div 
                className="flex justify-center items-center mb-6"
                initial={{ y: 10 }}
                animate={{ y: 0 }}
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, -10, 10, 0]
                  }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  className="relative"
                >
                  <div className="p-4 bg-red-100 rounded-full">
                    <Trash2 className="w-8 h-8 text-red-600" />
                  </div>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0, 1, 0]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity
                    }}
                    className="absolute -inset-2 bg-red-100 rounded-full -z-10"
                  />
                </motion.div>
              </motion.div>
              
              <div className="mb-6 text-center">
                <h3 className="flex gap-2 justify-center items-center mb-2 text-xl font-semibold text-gray-800">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Delete Notification
                </h3>
                
                <p className="mb-4 text-gray-600">
                  Are you sure you want to delete this notification? This action cannot be undone.
                </p>

                {/* Warning Message */}
                <div className="flex gap-2 items-center p-3 mb-4 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="flex-shrink-0 w-5 h-5 text-amber-500" />
                  <p className="text-sm text-left text-amber-700">
                    Deleted notifications cannot be recovered. Please confirm your action.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 justify-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setDeleteConfirmation({ show: false, notificationId: null })}
                  className="flex gap-2 items-center px-4 py-2 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                >
                  <XCircle size={18} />
                  Cancel
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={confirmDelete}
                  className="flex gap-2 items-center px-4 py-2 font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-colors hover:from-red-600 hover:to-red-700 shadow-red-500/30"
                >
                  <CheckCircle2 size={18} />
                  Delete
                </motion.button>
              </div>

              {/* Additional Info */}
              <div className="pt-4 mt-4 border-t border-gray-100">
                <p className="flex gap-1 justify-center items-center text-xs text-center text-gray-500">
                  <AlertCircle size={12} />
                  This action will permanently remove the notification from your inbox
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationsPage;